// src/components/AdminPanel.tsx
import React, { useState, useEffect } from 'react';
import { Settings, DollarSign, Wallet, AlertCircle, Check, Copy } from 'lucide-react';
import feeCollectionService from '../services/feeCollection.service';
import { useWallet } from '../contexts/WalletContext';

const ADMIN_WALLET = 'A8Kq1yNBW6pJKyH5qXmGTWgbU5b3KoZd1YnF9ebapX83'; // Your current wallet (change this to restrict access)

const AdminPanel: React.FC = () => {
  const { publicKey } = useWallet();
  const [isAdmin, setIsAdmin] = useState(false);
  const [wallets, setWallets] = useState({
    treasury: '',
    team: '',
    development: ''
  });
  const [balances, setBalances] = useState({
    treasury: 0,
    team: 0,
    development: 0,
    total: 0
  });
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    // Check if current user is admin
    if (publicKey === ADMIN_WALLET || publicKey === 'A8Kq1yNBW6pJKyH5qXmGTWgbU5b3KoZd1YnF9ebapX83') {
      setIsAdmin(true);
    }

    // Load saved wallets
    feeCollectionService.loadSavedWallets();
    const currentWallets = feeCollectionService.getPlatformWallets();
    setWallets({
      treasury: currentWallets.treasury,
      team: currentWallets.team,
      development: currentWallets.development
    });

    // Load balances
    loadBalances();
  }, [publicKey]);

  const loadBalances = async () => {
    const balances = await feeCollectionService.getPlatformBalances();
    setBalances(balances);
  };

  const handleSaveWallets = () => {
    feeCollectionService.updatePlatformWallets(wallets);
    setEditing(false);
    loadBalances();
    alert('✅ Platform wallets updated successfully!');
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const getFeeBreakdown = (amount: number) => {
    return feeCollectionService.getFeeBreakdown(amount);
  };

  if (!isAdmin) {
    return null; // Don't show admin panel to non-admins
  }

  const isConfigured = wallets.treasury !== 'YOUR_TREASURY_WALLET_ADDRESS_HERE';

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold flex items-center">
          <Settings className="w-6 h-6 mr-2 text-purple-400" />
          Admin Panel
        </h2>
        <div className="text-sm text-gray-400">
          Admin: {publicKey?.slice(0, 8)}...
        </div>
      </div>

      {!isConfigured && (
        <div className="mb-6 bg-red-900/20 border border-red-700/50 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <div className="font-medium text-red-400">Platform Wallets Not Configured!</div>
              <div className="text-sm text-red-400/70 mt-1">
                Fees are not being collected. Please configure your platform wallets below.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Wallets Configuration */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Platform Wallets</h3>
          <button
            onClick={() => setEditing(!editing)}
            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>

        <div className="space-y-4">
          {/* Treasury Wallet */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Treasury Wallet (50% of fees)
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={wallets.treasury}
                onChange={(e) => setWallets({ ...wallets, treasury: e.target.value })}
                disabled={!editing}
                className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 disabled:opacity-50"
                placeholder="Enter treasury wallet address..."
              />
              <button
                onClick={() => copyToClipboard(wallets.treasury, 'treasury')}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
              >
                {copied === 'treasury' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <div className="mt-1 text-sm text-gray-400">
              Balance: {balances.treasury.toFixed(4)} SOL
            </div>
          </div>

          {/* Team Wallet */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Team Wallet (30% of fees)
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={wallets.team}
                onChange={(e) => setWallets({ ...wallets, team: e.target.value })}
                disabled={!editing}
                className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 disabled:opacity-50"
                placeholder="Enter team wallet address..."
              />
              <button
                onClick={() => copyToClipboard(wallets.team, 'team')}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
              >
                {copied === 'team' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <div className="mt-1 text-sm text-gray-400">
              Balance: {balances.team.toFixed(4)} SOL
            </div>
          </div>

          {/* Development Wallet */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Development Wallet (15% of fees)
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={wallets.development}
                onChange={(e) => setWallets({ ...wallets, development: e.target.value })}
                disabled={!editing}
                className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 disabled:opacity-50"
                placeholder="Enter development wallet address..."
              />
              <button
                onClick={() => copyToClipboard(wallets.development, 'development')}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
              >
                {copied === 'development' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <div className="mt-1 text-sm text-gray-400">
              Balance: {balances.development.toFixed(4)} SOL
            </div>
          </div>

          {editing && (
            <button
              onClick={handleSaveWallets}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90"
            >
              Save Wallet Configuration
            </button>
          )}
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900/50 rounded-lg p-4">
          <DollarSign className="w-5 h-5 text-purple-400 mb-2" />
          <div className="text-2xl font-bold">{balances.total.toFixed(2)}</div>
          <div className="text-sm text-gray-400">Total Platform SOL</div>
        </div>
        
        <div className="bg-gray-900/50 rounded-lg p-4">
          <Wallet className="w-5 h-5 text-blue-400 mb-2" />
          <div className="text-2xl font-bold">{balances.treasury.toFixed(2)}</div>
          <div className="text-sm text-gray-400">Treasury SOL</div>
        </div>
        
        <div className="bg-gray-900/50 rounded-lg p-4">
          <Wallet className="w-5 h-5 text-green-400 mb-2" />
          <div className="text-2xl font-bold">{balances.team.toFixed(2)}</div>
          <div className="text-sm text-gray-400">Team SOL</div>
        </div>
        
        <div className="bg-gray-900/50 rounded-lg p-4">
          <Wallet className="w-5 h-5 text-yellow-400 mb-2" />
          <div className="text-2xl font-bold">{balances.development.toFixed(2)}</div>
          <div className="text-sm text-gray-400">Dev SOL</div>
        </div>
      </div>

      {/* Fee Breakdown */}
      <div className="bg-gray-900/50 rounded-lg p-4">
        <h3 className="font-medium mb-3">Fee Distribution Breakdown</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Token Creation Fee (0.1 SOL):</span>
          </div>
          <div className="ml-4 space-y-1 text-xs">
            <div className="flex justify-between">
              <span>→ Treasury (50%):</span>
              <span className="text-purple-400">0.05 SOL</span>
            </div>
            <div className="flex justify-between">
              <span>→ Team (30%):</span>
              <span className="text-green-400">0.03 SOL</span>
            </div>
            <div className="flex justify-between">
              <span>→ Development (15%):</span>
              <span className="text-yellow-400">0.015 SOL</span>
            </div>
            <div className="flex justify-between">
              <span>→ Referrer (5%):</span>
              <span className="text-blue-400">0.005 SOL</span>
            </div>
          </div>
          
          <div className="flex justify-between mt-3">
            <span className="text-gray-400">Bundler Fee (0.5 SOL):</span>
          </div>
          <div className="ml-4 space-y-1 text-xs">
            <div className="flex justify-between">
              <span>→ Treasury (50%):</span>
              <span className="text-purple-400">0.25 SOL</span>
            </div>
            <div className="flex justify-between">
              <span>→ Team (30%):</span>
              <span className="text-green-400">0.15 SOL</span>
            </div>
            <div className="flex justify-between">
              <span>→ Development (15%):</span>
              <span className="text-yellow-400">0.075 SOL</span>
            </div>
            <div className="flex justify-between">
              <span>→ Referrer (5%):</span>
              <span className="text-blue-400">0.025 SOL</span>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
        <h3 className="font-medium mb-2 text-blue-400">Important Notes</h3>
        <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
          <li>Platform wallets receive fees automatically when users create tokens or use tools</li>
          <li>5% of fees go to referrers (if user was referred)</li>
          <li>Wallet addresses are saved in localStorage (consider using a database for production)</li>
          <li>Make sure wallets are on the same network (Mainnet/Devnet)</li>
          <li>Only admin wallets can view and edit this panel</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPanel;